import { SimpleSpinner } from '@components/Loading/SimpleSpinner';
import { StepContent } from '@components/StepElements';
import { Toast } from '@components/Toast';
import { device } from '@constants/mediaQueries';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, css, Stack, styled } from '@mui/material';
import { useConfiguration } from '@providers/Configuration';
import { useTenant } from '@providers/Tenant';
import { useUser } from '@providers/User';
import { GA } from '@services/gtag';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useDecodedParams } from 'src/hooks/useDecodedParams';
import { useCurrentAndPreviousPartTreesQuery } from '../lib/hooks/useCurrentAndPreviousPartTreesQuery';
import { useUpdatePartCategoriesJob } from '../lib/hooks/useUpdatePartCategoriesJob';
import { PartCategoriesTree } from './PartCategoriesTree';

interface GTagEventData {
  user: string;
  account: string;
  product: string;
  role: string;
}

const uploadNewCategoriesGTag = (eventData: GTagEventData) => {
  GA.gtag('event', 'manage_product_parts_categories_to_upload_new_categories', eventData);
};

/* istanbul ignore next - not worth testing */
const viewPartsCatalogueGTag = (eventData: GTagEventData) => {
  GA.gtag('event', 'manage_product_parts_categories_to_parts_catalogue', eventData);
};

export function PartsCategoriesPreview({ runID }: { runID: string }) {
  const { parentPID: productPID } = useConfiguration();
  const location = useLocation();
  const searchParams = new URLSearchParams(location?.search);
  const { accountId } = useTenant();
  const { role, username } = useUser();
  const { productId } = useDecodedParams<{ productId: string }>();
  const configurationPID = searchParams.get('configurationPID');
  const { t } = useTranslation('manageProduct');

  const eventData: GTagEventData = useMemo(
    () => ({
      user: username!,
      account: accountId,
      product: productId,
      role: role!,
    }),
    [username, accountId, productId, role]
  );

  useEffect(() => {
    GA.gtag('event', 'manage_product_parts_categories_completed', eventData);
  }, [accountId, productId, eventData]);

  const { data, isError, isLoading } = useCurrentAndPreviousPartTreesQuery({
    account: accountId,
    configurationPID: configurationPID!,
    productPID,
  });

  const {
    mutateAsync: updatePartsCategoriesJob,
    isLoading: isPatchLoading,
    isError: isPatchError,
  } = useUpdatePartCategoriesJob();

  const handleUploadNewCategories = async () => {
    await updatePartsCategoriesJob({ accountId, runID, status: 'COMPLETED' });

    uploadNewCategoriesGTag(eventData);
  };

  return (
    <>
      <StepContent>
        <Stack direction="column" spacing={4} height="100%">
          <Stack
            direction={{ sm: 'column', md: 'row' }}
            divider={<VerticalLine />}
            spacing={4}
            rowGap={4}
            height="100%"
          >
            <PartCategoriesTree
              data={data?.previous}
              isError={isError}
              isLoading={isLoading}
              titleTranslationKey="manageProduct:partsCategories.previousPartsCategories.treeTitle"
            />
            <PartCategoriesTree
              data={data?.current}
              isError={isError}
              isLoading={isLoading}
              titleTranslationKey="manageProduct:partsCategories.currentPartsCategories.treeTitle"
            >
              <StyledPartCatalogueButton
                variant="contained"
                color="primary"
                size="medium"
                href={`/product-library/${productId}`}
                onClick={/* istanbul ignore next - not worth testing */ () => viewPartsCatalogueGTag(eventData)}
              >
                {t('manageProduct:partsCategories.currentPartsCategories.viewPartsCatalog')}
              </StyledPartCatalogueButton>
            </PartCategoriesTree>
          </Stack>
          <ButtonContainer>
            <StyledUploadCategoriesButton
              variant="contained"
              color="primary"
              size="large"
              endIcon={
                isPatchLoading ? /* istanbul ignore next */ null : <FontAwesomeIcon icon={faArrowRight} size="3x" />
              }
              onClick={() => handleUploadNewCategories()}
              disabled={isPatchLoading}
            >
              {isPatchLoading ? (
                /* istanbul ignore next */ <SimpleSpinner />
              ) : (
                t('partsCategories.review.uploadNewPartsCategories')
              )}
            </StyledUploadCategoriesButton>
          </ButtonContainer>
        </Stack>
      </StepContent>
      <Toast
        dependency={isPatchError}
        severity="error"
        title={`${t('partsCategories.review.errorCompletingReview')}`}
        data-testid="toast-notification"
      />
    </>
  );
}

const VerticalLine = styled('div')`
  width: 2px;
  height: 100%;
  background-color: ${({ theme }) => theme.palette.grey['400']};
  @media only screen and ${device.mobileOrTablet} {
    display: none;
  }
`;

const ButtonContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  @media only screen and ${device.mobileOrTablet} {
    justify-content: flex-start;
  }
`;

const BaseButton = styled(Button)`
  text-transform: unset;
  ${({ theme }) => css`
    font-weight: ${theme.typography.fontWeightRegular};
    font-size: ${theme.typography.body1.fontSize};
    @media only screen and ${device.mobileOrTablet} {
      font-size: ${theme.typography.body2.fontSize};
    }
    &:hover {
      background-color: ${theme.palette.secondary.light};
    }
  `};
`;

const StyledPartCatalogueButton = styled(BaseButton)`
  margin-left: auto;
  text-align: center;
`;

const StyledUploadCategoriesButton = styled(BaseButton)`
  .MuiButton-endIcon {
    margin-left: 1rem;
  }
`;
